import {
  type ReactEventHandler,
  type ReactNode,
  createContext,
  useContext,
} from "react";
import {
  Box,
  Column,
  HtMedia,
  HtVideo,
  Row,
  Text,
  Tooltip,
} from "@hightouch/htui";
import { CreativeCarouselControls } from "src/pages/ad-studio/insights/creative-carousel-controls";
import {
  type YouTubeClicks,
  YouTubePlayer,
} from "src/pages/ad-studio/insights/youtube-embed";
import type {
  CardSelection,
  MediaItem,
  VideoSource,
} from "src/pages/ad-studio/insights/performance/types";
import type { EntityCardRootState } from "src/pages/ad-studio/insights/performance/view/types";

type RootProps = EntityCardRootState & { children?: ReactNode };

type RegionProps = { children?: ReactNode };

type EntityCardContextValue = EntityCardRootState & {
  youtubeClicks: YouTubeClicks;
};

const EntityCardContext = createContext<EntityCardContextValue | undefined>(
  undefined,
);

const useEntityCardContext = (part: string) => {
  const context = useContext(EntityCardContext);
  if (context === undefined) {
    throw new Error(
      `\`EntityCardPrimitive.${part}\` must be used within \`EntityCardPrimitive.Root\``,
    );
  }
  return context;
};

const CardProvider = ({
  name,
  media,
  performAction,
  youtubeClicks,
  children,
}: EntityCardContextValue & { children?: ReactNode }) => (
  <EntityCardContext.Provider
    value={{ name, media, performAction, youtubeClicks }}
  >
    {children}
  </EntityCardContext.Provider>
);

const Root = ({ name, media, performAction, children }: RootProps) => (
  <CardProvider
    name={name}
    media={media}
    performAction={performAction}
    youtubeClicks="stay-in-player"
  >
    {children}
  </CardProvider>
);

/** A card in a grid that selects one: a playing player swallows clicks, so only the selected card keeps them. */
const SelectableRoot = ({
  name,
  media,
  performAction,
  selection,
  children,
}: RootProps & { selection: CardSelection }) => (
  <CardProvider
    name={name}
    media={media}
    performAction={performAction}
    youtubeClicks={selection === "selected" ? "stay-in-player" : "reach-card"}
  >
    {children}
  </CardProvider>
);

const Media = ({ children }: RegionProps) => (
  <Box position="relative">{children}</Box>
);

type MediaEvents = {
  onLoad?: ReactEventHandler<HTMLElement>;
  onLoadedMetadata?: ReactEventHandler<HTMLElement>;
  onError?: ReactEventHandler<HTMLElement>;
};

const NaturalCreative = ({
  onLoad,
  onLoadedMetadata,
  onError,
}: MediaEvents) => {
  const { name, media } = useEntityCardContext("NaturalCreative");
  return (
    <MediaItemView
      item={media.shown.item}
      name={name}
      onLoad={onLoad}
      onLoadedMetadata={onLoadedMetadata}
      onError={onError}
    />
  );
};

const Creative = ({ onLoad, onLoadedMetadata, onError }: MediaEvents) => (
  <Box
    sx={{
      "--entity-card-aspect-ratio": "1 / 1",
      "--entity-card-video-height": "100%",
    }}
  >
    <NaturalCreative
      onLoad={onLoad}
      onLoadedMetadata={onLoadedMetadata}
      onError={onError}
    />
  </Box>
);

const Carousel = () => {
  const { media, performAction } = useEntityCardContext("Carousel");
  switch (media.kind) {
    case "single": {
      return null;
    }
    case "carousel": {
      return (
        <CreativeCarouselControls
          tone="on-media"
          carousel={media}
          performAction={performAction}
        />
      );
    }
    default: {
      return media satisfies never;
    }
  }
};

const Pills = ({ children }: RegionProps) => (
  <Box
    position="absolute"
    left={2}
    right={2}
    top={2}
    // The pills are labels, and their row spans the whole tile.
    // Hit-testing it would end a video's hover scrub mid-sweep.
    pointerEvents="none"
  >
    {children}
  </Box>
);

const Details = ({ children }: RegionProps) => (
  <Column p={2} gap={2} minWidth={0} overflow="hidden">
    {children}
  </Column>
);

const Name = ({ children }: RegionProps) => (
  <Row width="100%" minWidth={0}>
    <Tooltip message={children}>
      <Box
        width="100%"
        minWidth={0}
        sx={{
          display: "-webkit-box",
          WebkitBoxOrient: "vertical",
          WebkitLineClamp: 2,
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        <Text
          fontWeight="medium"
          overflowWrap="anywhere"
          wordBreak="break-word"
        >
          {children}
        </Text>
      </Box>
    </Tooltip>
  </Row>
);

const CardVideo = ({
  video,
  onLoadedMetadata,
  onError,
}: Omit<MediaEvents, "onLoad"> & { video: VideoSource }) => {
  const { youtubeClicks } = useEntityCardContext("NaturalCreative");
  switch (video.kind) {
    case "youtube": {
      return (
        <YouTubePlayer
          key={video.videoId}
          clicks={youtubeClicks}
          videoId={video.videoId}
          thumbnailUrl={video.thumbnailUrl}
          aspectRatio="var(--entity-card-aspect-ratio, 16 / 9)"
        />
      );
    }
    case "file": {
      return (
        <HtVideo
          decorative
          key={video.url}
          src={video.url}
          playback="scrub"
          showDuration
          loading="lazy"
          width="100%"
          aspectRatio="var(--entity-card-aspect-ratio, auto)"
          __css={{ height: "var(--entity-card-video-height, auto)" }}
          objectFit="cover"
          bg="base.border"
          onLoadedMetadata={onLoadedMetadata}
          onError={onError}
        />
      );
    }
    default: {
      return video satisfies never;
    }
  }
};

const MediaItemView = ({
  item,
  name,
  onLoad,
  onLoadedMetadata,
  onError,
}: MediaEvents & { item: MediaItem; name: string }) => {
  switch (item.kind) {
    case "video": {
      return (
        <CardVideo
          video={item.video}
          onLoadedMetadata={onLoadedMetadata}
          onError={onError}
        />
      );
    }
    case "image": {
      return (
        <HtMedia
          src={item.url}
          alt={name}
          width="100%"
          sx={{ aspectRatio: "var(--entity-card-aspect-ratio, auto)" }}
          objectFit="cover"
          bg="base.border"
          onLoad={onLoad}
          onError={onError}
        />
      );
    }
    default: {
      return item satisfies never;
    }
  }
};

export const EntityCardPrimitive = {
  Root,
  SelectableRoot,
  Media,
  Creative,
  NaturalCreative,
  Carousel,
  Pills,
  Details,
  Name,
};
